import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cloneDeep, set, debounce } from 'lodash';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { LogOut, Save, RotateCcw, Upload, Trash2, ArrowUp, ArrowDown, Plus, X, Eye, Send, Undo2, History, Lock, Settings, Palette, CreditCard, FileText, RefreshCw } from 'lucide-react';
import ProductGalleryManager from '@/components/admin/ProductGalleryManager';

const THEME_PRESETS = {
  Concrete: { bg: '#DCDDD8', text: '#1C1F26' },
  Midnight: { bg: '#15171C', text: '#E7E8E3' },
  Blush: { bg: '#E8D8D4', text: '#2A1514' },
};
const HEADLINE_FONTS = ['Bodoni Moda', 'Anton', 'Fraunces', 'Playfair Display', 'Bebas Neue', 'DM Serif Display', 'Space Grotesk', 'Archivo Black'];
const BODY_FONTS = ['Archivo', 'Inter', 'DM Sans', 'Work Sans'];
const COVER_LAYOUTS = [{ id: 'center', label: 'Center with cover lines' }, { id: 'full-width', label: 'Full-width photo' }];
const CREATOR_LAYOUTS = [{ id: 'portrait-left', label: 'Portrait left' }, { id: 'portrait-right', label: 'Portrait right' }];
const LOOKBOOK_LAYOUTS = [{ id: 'staggered', label: 'Staggered' }, { id: 'even', label: 'Even grid' }, { id: 'large-first', label: 'Large first' }];
const FOLLOW_LAYOUTS = [{ id: 'large-text', label: 'Large text list' }, { id: 'button-row', label: 'Button row' }];
const SECTION_LABELS = { top_strip: 'Top Strip', masthead: 'Masthead', cover: 'Cover', creator: 'Meet the Creator', lookbook: 'Lookbook', store: 'Store', social_links: 'Follow Along', partnerships: 'Brand Partnerships', footer: 'Footer' };

function FG({ label, children, className = '' }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-neutral-400 text-xs uppercase tracking-widest">{label}</Label>{children}</div>;
}
function SH({ title }) {
  return <h3 className="text-white text-sm font-semibold uppercase tracking-wider border-b border-neutral-800 pb-2 mb-4 mt-6 first:mt-0">{title}</h3>;
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // saved | saving | unsaved
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [stripeMode, setStripeMode] = useState(null);
  const fileRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user === false)) navigate('/teddyfuezing/davidsidea');
  }, [user, loading, navigate]);

  const fetchDraft = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/draft');
      setContent(data);
      setSaveStatus('saved');
    } catch { toast.error('Failed to load content'); }
    finally { setFetching(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try { const { data } = await api.get('/admin/history'); setHistory(data); } catch {}
  }, []);

  const fetchAdmins = useCallback(async () => {
    try { const { data } = await api.get('/admin/users'); setAdmins(data); } catch {}
  }, []);

  const fetchShopProducts = useCallback(async () => {
    try { const { data } = await api.get('/admin/shop/products'); setShopProducts(data.products || []); } catch {}
  }, []);

  const fetchStripeMode = useCallback(async () => {
    try { const { data } = await api.get('/admin/stripe-mode'); setStripeMode(data); } catch {}
  }, []);

  useEffect(() => { fetchDraft(); fetchHistory(); fetchAdmins(); fetchShopProducts(); fetchStripeMode(); }, [fetchDraft, fetchHistory, fetchAdmins, fetchShopProducts, fetchStripeMode]);

  // Auto-save draft (debounced)
  const saveDraftRef = useRef();
  saveDraftRef.current = async (data) => {
    setSaveStatus('saving');
    try { await api.put('/admin/draft', data); setSaveStatus('saved'); }
    catch { setSaveStatus('unsaved'); toast.error('Auto-save failed'); }
  };
  const debouncedSave = useRef(debounce((data) => saveDraftRef.current(data), 2000)).current;

  const updateField = (path, value) => {
    setContent(prev => {
      const n = cloneDeep(prev);
      set(n, path, value);
      setSaveStatus('unsaved');
      debouncedSave(n);
      return n;
    });
  };

  // Manual save
  const saveDraft = async () => {
    debouncedSave.cancel();
    setSaveStatus('saving');
    try { await api.put('/admin/draft', content); setSaveStatus('saved'); toast.success('Draft saved'); }
    catch { setSaveStatus('unsaved'); toast.error('Save failed'); }
  };

  const publishDraft = async () => {
    setPublishing(true);
    try {
      debouncedSave.cancel();
      await api.put('/admin/draft', content);
      await api.post('/admin/publish');
      setSaveStatus('saved');
      toast.success('Published! Changes are live.');
      fetchHistory();
    } catch { toast.error('Publish failed'); }
    finally { setPublishing(false); }
  };

  const discardDraft = async () => {
    try { await api.post('/admin/discard'); await fetchDraft(); toast.success('Draft discarded'); }
    catch { toast.error('Discard failed'); }
  };

  const resetDefaults = async () => {
    try { await api.post('/admin/reset'); await fetchDraft(); toast.success('Reset to defaults'); }
    catch { toast.error('Reset failed'); }
  };

  const restoreVersion = async (id) => {
    try { await api.post(`/admin/restore/${id}`); await fetchDraft(); toast.success('Version restored to draft'); }
    catch { toast.error('Restore failed'); }
  };

  // Unsaved changes warning
  useEffect(() => {
    const handler = (e) => { if (saveStatus === 'unsaved') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus]);

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    const fd = new FormData(); fd.append('file', file); fd.append('alt', ''); fd.append('caption', '');
    setUploading(true);
    try {
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (uploadTarget.type === 'cover') updateField('cover.image_url', data.url);
      else if (uploadTarget.type === 'portrait') updateField('creator.portrait_url', data.url);
      else if (uploadTarget.type === 'lookbook-add') {
        setContent(prev => { const n = cloneDeep(prev); n.lookbook.photos.push({ id: data.id, url: data.url, thumb_url: data.thumb_url, alt: '', title: '', caption: '' }); setSaveStatus('unsaved'); debouncedSave(n); return n; });
      } else if (uploadTarget.type === 'lookbook-replace') updateField(`lookbook.photos.${uploadTarget.index}.url`, data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); setUploadTarget(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  const triggerUpload = (target) => { setUploadTarget(target); setTimeout(() => fileRef.current?.click(), 50); };

  const deleteLookbookPhoto = async (idx) => {
    const photo = content.lookbook.photos[idx];
    try { await api.delete('/admin/image', { data: { url: photo.url, thumb_url: photo.thumb_url || '' } }); } catch {}
    setContent(prev => { const n = cloneDeep(prev); n.lookbook.photos.splice(idx, 1); setSaveStatus('unsaved'); debouncedSave(n); return n; });
  };

  const moveLookbook = (idx, dir) => {
    setContent(prev => { const n = cloneDeep(prev); const arr = n.lookbook.photos; const ni = idx + dir; if (ni < 0 || ni >= arr.length) return prev; [arr[idx], arr[ni]] = [arr[ni], arr[idx]]; setSaveStatus('unsaved'); debouncedSave(n); return n; });
  };

  const moveSection = (idx, dir) => {
    setContent(prev => {
      const n = cloneDeep(prev);
      const arr = n.layout.sections_order;
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return prev;
      // Don't move top_strip or masthead from top
      if ((arr[idx] === 'top_strip' || arr[idx] === 'masthead') && dir > 0 && idx < 2) return prev;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      setSaveStatus('unsaved'); debouncedSave(n); return n;
    });
  };

  const toggleSection = (key) => {
    setContent(prev => {
      const n = cloneDeep(prev);
      const hidden = n.layout.hidden_sections || [];
      const idx = hidden.indexOf(key);
      if (idx >= 0) hidden.splice(idx, 1); else hidden.push(key);
      n.layout.hidden_sections = hidden;
      setSaveStatus('unsaved'); debouncedSave(n); return n;
    });
  };

  const handleLogout = async () => { await logout(); navigate('/teddyfuezing/davidsidea'); };

  if (loading || fetching) return <div className="min-h-screen flex items-center justify-center bg-neutral-950"><span className="text-neutral-500 text-xs uppercase tracking-widest">Loading dashboard...</span></div>;
  if (!content) return null;

  const IC = "bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 text-sm";
  const statusColor = saveStatus === 'saved' ? 'text-green-400' : saveStatus === 'saving' ? 'text-yellow-400' : 'text-orange-400';
  const statusText = saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving...' : 'Unsaved changes';

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-neutral-950 text-white">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageUpload} />

      {/* Header */}
      <div className="border-b border-neutral-800 px-4 sm:px-6 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h1 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic' }}>Admin</h1>
          <span className={`text-xs uppercase tracking-widest ${statusColor}`}>{statusText}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="save-draft-button" onClick={saveDraft} variant="outline" size="sm" className="text-xs uppercase tracking-widest border-neutral-700 text-neutral-300 hover:text-white gap-1"><Save size={14} /> Save Draft</Button>
          <a href="/?preview=1" target="_blank" rel="noopener noreferrer"><Button data-testid="preview-button" variant="outline" size="sm" className="text-xs uppercase tracking-widest border-neutral-700 text-neutral-300 hover:text-white gap-1"><Eye size={14} /> Preview</Button></a>
          <Button data-testid="publish-button" onClick={publishDraft} disabled={publishing} size="sm" className="bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest gap-1"><Send size={14} /> {publishing ? 'Publishing...' : 'Publish'}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-neutral-500 hover:text-white gap-1"><Undo2 size={14} /> Discard</Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-neutral-900 border-neutral-800">
              <AlertDialogHeader><AlertDialogTitle className="text-white">Discard draft?</AlertDialogTitle><AlertDialogDescription className="text-neutral-400">This reverts to the last published version.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="bg-neutral-800 text-white hover:bg-neutral-700 border-neutral-700">Cancel</AlertDialogCancel><AlertDialogAction onClick={discardDraft} className="bg-red-600 text-white hover:bg-red-700">Discard</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button data-testid="logout-button" onClick={handleLogout} variant="ghost" size="sm" className="text-neutral-400 hover:text-white gap-1"><LogOut size={14} /></Button>
          {stripeMode && (
            <span data-testid="stripe-mode-badge" className={`ml-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${stripeMode.mismatch ? 'bg-red-900 text-red-300' : stripeMode.mode === 'live' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
              <CreditCard size={10} className="inline mr-1" />{stripeMode.mismatch ? 'KEY MISMATCH' : stripeMode.mode === 'live' ? 'LIVE' : 'TEST'} MODE
            </span>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {uploading && <div className="mb-4 p-3 bg-blue-950 border border-blue-800 text-blue-300 text-xs uppercase tracking-widest text-center rounded">Uploading...</div>}
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="bg-neutral-900 border border-neutral-800 w-full justify-start flex-wrap h-auto p-1 gap-0.5">
            {['content', 'layout', 'images', 'appearance', 'links', 'shop', 'legal', 'seo', 'history', 'settings'].map(t => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="text-xs uppercase tracking-widest data-[state=active]:bg-neutral-800 data-[state=active]:text-white text-neutral-500 px-3 py-2">{t}</TabsTrigger>
            ))}
          </TabsList>

          {/* ========== CONTENT TAB ========== */}
          <TabsContent value="content" className="mt-6 space-y-4">
            <SH title="Top Strip" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FG label="Left"><Input value={content.top_strip?.left || ''} onChange={e => updateField('top_strip.left', e.target.value)} className={IC} /></FG>
              <FG label="Center"><Input value={content.top_strip?.center || ''} onChange={e => updateField('top_strip.center', e.target.value)} className={IC} /></FG>
              <FG label="Right"><Input value={content.top_strip?.right || ''} onChange={e => updateField('top_strip.right', e.target.value)} className={IC} /></FG>
            </div>
            <SH title="Masthead" />
            <FG label="Name"><Input value={content.masthead || ''} onChange={e => updateField('masthead', e.target.value)} className={IC} /></FG>
            <SH title="Cover Lines" />
            {content.cover?.lines?.map((line, i) => (
              <div key={i} className="grid grid-cols-3 gap-3">
                <FG label={`Line ${i + 1}`} className="col-span-2"><Input value={line.text} onChange={e => updateField(`cover.lines.${i}.text`, e.target.value)} className={IC} /></FG>
                <FG label="Label"><Input value={line.label} onChange={e => updateField(`cover.lines.${i}.label`, e.target.value)} className={IC} /></FG>
              </div>
            ))}
            <FG label="Cover Caption"><Input value={content.cover?.caption || ''} onChange={e => updateField('cover.caption', e.target.value)} className={IC} /></FG>
            <SH title="Meet the Creator" />
            <FG label="Section Title"><Input value={content.creator?.section_title || ''} onChange={e => updateField('creator.section_title', e.target.value)} className={IC} /></FG>
            {content.creator?.bio?.map((p, i) => (
              <FG key={i} label={`Bio Paragraph ${i + 1}`}><Textarea value={p} onChange={e => updateField(`creator.bio.${i}`, e.target.value)} className={`${IC} min-h-[80px]`} /></FG>
            ))}
            <FG label="Pull Quote"><Textarea value={content.creator?.pull_quote || ''} onChange={e => updateField('creator.pull_quote', e.target.value)} className={`${IC} min-h-[60px]`} /></FG>
            <SH title="Store / Coming Soon Section" />
            <div className="flex items-center gap-3 mb-3">
              <Switch checked={content.store?.open === true} onCheckedChange={v => updateField('store.open', v)} />
              <span className="text-xs text-neutral-400 uppercase tracking-widest">{content.store?.open ? 'Store Open' : 'Coming Soon'}</span>
            </div>
            <FG label="Section Label"><Input value={content.store?.label || ''} onChange={e => updateField('store.label', e.target.value)} className={IC} /></FG>
            <FG label="Store Title (when open)"><Input value={content.store?.title || ''} onChange={e => updateField('store.title', e.target.value)} className={IC} /></FG>
            <FG label="Store Subtitle"><Input value={content.store?.subtitle || ''} onChange={e => updateField('store.subtitle', e.target.value)} className={IC} /></FG>
            <p className="text-neutral-500 text-xs mt-4 mb-2 uppercase tracking-widest">Teaser (shown when Coming Soon)</p>
            <FG label="Teaser Title"><Input value={content.store?.teaser_title || ''} onChange={e => updateField('store.teaser_title', e.target.value)} className={IC} /></FG>
            <FG label="Teaser Description"><Textarea value={content.store?.teaser_description || ''} onChange={e => updateField('store.teaser_description', e.target.value)} className={`${IC} min-h-[60px]`} /></FG>
            <FG label="Teaser Badge"><Input value={content.store?.teaser_badge || ''} onChange={e => updateField('store.teaser_badge', e.target.value)} className={IC} /></FG>
            <p className="text-neutral-500 text-xs mt-4 mb-2 uppercase tracking-widest">Store Settings</p>
            <FG label="Shipping Note"><Input value={content.store?.shipping_note || ''} onChange={e => updateField('store.shipping_note', e.target.value)} className={IC} /></FG>
            <FG label="Size Chart"><Textarea value={content.store?.size_chart || ''} onChange={e => updateField('store.size_chart', e.target.value)} className={`${IC} min-h-[40px]`} /></FG>
            <FG label="Flat Shipping Rate ($) (fallback)"><Input value={content.store?.flat_shipping_rate || ''} onChange={e => updateField('store.flat_shipping_rate', e.target.value)} className={IC} /></FG>
            <FG label="Shipping Label"><Input value={content.store?.flat_shipping_label || ''} onChange={e => updateField('store.flat_shipping_label', e.target.value)} className={IC} /></FG>
            <p className="text-neutral-500 text-xs mt-4 mb-2 uppercase tracking-widest">Shipping Rates by Product Type</p>
            {['tee', 'mug', 'sticker', 'default'].map(t => {
              const tier = content.store?.shipping_tiers?.[t] || {};
              return (
                <div key={t} className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-neutral-400 w-16 uppercase">{tier.label || t}</span>
                  <Input value={tier.first || ''} onChange={e => updateField(`store.shipping_tiers.${t}.first`, e.target.value)} className={IC + ' w-20'} placeholder="First" />
                  <span className="text-xs text-neutral-600">+</span>
                  <Input value={tier.extra || ''} onChange={e => updateField(`store.shipping_tiers.${t}.extra`, e.target.value)} className={IC + ' w-20'} placeholder="Extra" />
                  <span className="text-xs text-neutral-600">per extra item</span>
                </div>
              );
            })}
            <FG label="Allowed Countries (comma-separated ISO codes)"><Input value={(content.store?.allowed_countries || []).join(', ')} onChange={e => updateField('store.allowed_countries', e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))} className={IC} placeholder="US, CA, GB" /></FG>
            <p className="text-neutral-500 text-xs mt-4 mb-2 uppercase tracking-widest">Sticker Add-on (Cart Suggestion)</p>
            <div className="flex items-center gap-3 mb-3">
              <Switch checked={content.store?.sticker_addon?.enabled === true} onCheckedChange={v => updateField('store.sticker_addon.enabled', v)} />
              <span className="text-xs text-neutral-400 uppercase tracking-widest">{content.store?.sticker_addon?.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <FG label="Add-on Product ID (Printful ID)"><Input type="number" value={content.store?.sticker_addon?.product_id || ''} onChange={e => updateField('store.sticker_addon.product_id', parseInt(e.target.value) || null)} className={IC} placeholder="e.g. 488532197" /></FG>
            <FG label="Add-on Label"><Input value={content.store?.sticker_addon?.label || ''} onChange={e => updateField('store.sticker_addon.label', e.target.value)} className={IC} placeholder="Add a sticker?" /></FG>
            <SH title="Brand Partnerships" />
            <FG label="Section Title"><Input value={content.partnerships?.section_title || ''} onChange={e => updateField('partnerships.section_title', e.target.value)} className={IC} /></FG>
            <FG label="Intro Text"><Textarea value={content.partnerships?.intro || ''} onChange={e => updateField('partnerships.intro', e.target.value)} className={`${IC} min-h-[60px]`} /></FG>
            {content.partnerships?.offerings?.map((item, i) => (
              <div key={i} className="flex gap-2 items-end">
                <FG label={i === 0 ? 'Offerings' : ''} className="flex-1"><Input value={item} onChange={e => updateField(`partnerships.offerings.${i}`, e.target.value)} className={IC} /></FG>
                <Button variant="ghost" size="sm" onClick={() => { setContent(prev => { const n = cloneDeep(prev); n.partnerships.offerings.splice(i, 1); setSaveStatus('unsaved'); debouncedSave(n); return n; }); }} className="text-red-400 hover:text-red-300 mb-0.5"><X size={14} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => { setContent(prev => { const n = cloneDeep(prev); n.partnerships.offerings.push('New offering'); setSaveStatus('unsaved'); debouncedSave(n); return n; }); }} className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><Plus size={14} /> Add</Button>
            <FG label="Button Label"><Input value={content.partnerships?.button_label || ''} onChange={e => updateField('partnerships.button_label', e.target.value)} className={IC} /></FG>
            <FG label="Contact Email"><Input value={content.partnerships?.contact_email || ''} onChange={e => updateField('partnerships.contact_email', e.target.value)} className={IC} /></FG>
            <SH title="Footer" />
            <FG label="Footer Text"><Input value={content.footer?.text || ''} onChange={e => updateField('footer.text', e.target.value)} className={IC} /></FG>
          </TabsContent>

          {/* ========== LAYOUT TAB ========== */}
          <TabsContent value="layout" className="mt-6 space-y-4">
            <SH title="Section Order & Visibility" />
            <p className="text-neutral-500 text-xs mb-4">Reorder sections and toggle visibility. Masthead and top strip stay at the top.</p>
            <div className="space-y-2">
              {(content.layout?.sections_order || []).map((key, idx) => (
                <div key={key} className="flex items-center gap-2 p-2 bg-neutral-900 border border-neutral-800 rounded">
                  <div className="flex flex-col gap-0.5">
                    <Button variant="ghost" size="icon" onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-neutral-500 hover:text-white h-6 w-6"><ArrowUp size={12} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => moveSection(idx, 1)} disabled={idx === (content.layout?.sections_order?.length || 0) - 1} className="text-neutral-500 hover:text-white h-6 w-6"><ArrowDown size={12} /></Button>
                  </div>
                  <span className="flex-1 text-sm">{SECTION_LABELS[key] || key}</span>
                  <Switch checked={!(content.layout?.hidden_sections || []).includes(key)} onCheckedChange={() => toggleSection(key)} />
                </div>
              ))}
            </div>
            <SH title="Layout Presets" />
            <FG label="Cover Layout">
              <div className="flex gap-2">{COVER_LAYOUTS.map(l => (
                <button key={l.id} onClick={() => updateField('layout.cover_layout', l.id)} className={`px-3 py-2 text-xs rounded border ${content.layout?.cover_layout === l.id ? 'border-blue-500 bg-blue-950 text-white' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{l.label}</button>
              ))}</div>
            </FG>
            <FG label="Creator Layout">
              <div className="flex gap-2">{CREATOR_LAYOUTS.map(l => (
                <button key={l.id} onClick={() => updateField('layout.creator_layout', l.id)} className={`px-3 py-2 text-xs rounded border ${content.layout?.creator_layout === l.id ? 'border-blue-500 bg-blue-950 text-white' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{l.label}</button>
              ))}</div>
            </FG>
            <FG label="Lookbook Layout">
              <div className="flex gap-2 flex-wrap">{LOOKBOOK_LAYOUTS.map(l => (
                <button key={l.id} onClick={() => updateField('layout.lookbook_layout', l.id)} className={`px-3 py-2 text-xs rounded border ${content.layout?.lookbook_layout === l.id ? 'border-blue-500 bg-blue-950 text-white' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{l.label}</button>
              ))}</div>
            </FG>
            <FG label="Lookbook Columns">
              <div className="flex gap-2">{[2, 3].map(c => (
                <button key={c} onClick={() => updateField('layout.lookbook_columns', c)} className={`px-4 py-2 text-xs rounded border ${content.layout?.lookbook_columns === c ? 'border-blue-500 bg-blue-950 text-white' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{c} columns</button>
              ))}</div>
            </FG>
            <FG label="Follow Along Layout">
              <div className="flex gap-2">{FOLLOW_LAYOUTS.map(l => (
                <button key={l.id} onClick={() => updateField('layout.follow_layout', l.id)} className={`px-3 py-2 text-xs rounded border ${content.layout?.follow_layout === l.id ? 'border-blue-500 bg-blue-950 text-white' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{l.label}</button>
              ))}</div>
            </FG>
          </TabsContent>

          {/* ========== IMAGES TAB ========== */}
          <TabsContent value="images" className="mt-6 space-y-4">
            <SH title="Cover Image" />
            <div className="flex gap-4 items-start">
              {content.cover?.image_url && <img src={content.cover.image_url} alt="Cover" className="w-32 h-40 object-cover rounded border border-neutral-800" />}
              <div className="space-y-2 flex-1">
                <Button onClick={() => triggerUpload({ type: 'cover' })} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><Upload size={14} /> Replace</Button>
                <FG label="Alt Text"><Input value={content.cover?.image_alt || ''} onChange={e => updateField('cover.image_alt', e.target.value)} className={IC} /></FG>
              </div>
            </div>
            <SH title="Portrait Image" />
            <div className="flex gap-4 items-start">
              {content.creator?.portrait_url && <img src={content.creator.portrait_url} alt="Portrait" className="w-32 h-40 object-cover rounded border border-neutral-800" />}
              <div className="space-y-2 flex-1">
                <Button onClick={() => triggerUpload({ type: 'portrait' })} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><Upload size={14} /> Replace</Button>
                <FG label="Alt Text"><Input value={content.creator?.portrait_alt || ''} onChange={e => updateField('creator.portrait_alt', e.target.value)} className={IC} /></FG>
              </div>
            </div>
            <SH title="Lookbook Photos" />
            {content.lookbook?.photos?.map((photo, idx) => (
              <div key={photo.id || idx} className="flex gap-3 items-start p-3 bg-neutral-900 border border-neutral-800 rounded">
                <img src={photo.thumb_url || photo.url} alt={photo.alt || photo.title} className="w-20 h-24 object-cover rounded flex-shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <FG label="Title"><Input value={photo.title || ''} onChange={e => updateField(`lookbook.photos.${idx}.title`, e.target.value)} className={IC} /></FG>
                  <FG label="Caption"><Input value={photo.caption || ''} onChange={e => updateField(`lookbook.photos.${idx}.caption`, e.target.value)} className={IC} /></FG>
                  <FG label="Alt"><Input value={photo.alt || ''} onChange={e => updateField(`lookbook.photos.${idx}.alt`, e.target.value)} className={IC} /></FG>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => moveLookbook(idx, -1)} disabled={idx === 0} className="text-neutral-500 hover:text-white h-7 w-7"><ArrowUp size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => moveLookbook(idx, 1)} disabled={idx === content.lookbook.photos.length - 1} className="text-neutral-500 hover:text-white h-7 w-7"><ArrowDown size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => triggerUpload({ type: 'lookbook-replace', index: idx })} className="text-neutral-500 hover:text-white h-7 w-7"><Upload size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteLookbookPhoto(idx)} className="text-red-400 hover:text-red-300 h-7 w-7"><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
            <Button onClick={() => triggerUpload({ type: 'lookbook-add' })} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><Plus size={14} /> Add Photo</Button>
          </TabsContent>

          {/* ========== APPEARANCE TAB ========== */}
          <TabsContent value="appearance" className="mt-6 space-y-6">
            <SH title="Theme" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[...Object.entries(THEME_PRESETS), ['Custom', null]].map(([name, colors]) => (
                <button key={name} data-testid={`theme-selector-${name.toLowerCase()}`} onClick={() => updateField('settings.theme', name)}
                  className={`p-3 rounded border-2 transition-all ${content.settings?.theme === name ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-neutral-800 hover:border-neutral-600'}`}>
                  {colors ? (
                    <div className="h-12 rounded mb-2 flex items-center justify-center" style={{ backgroundColor: colors.bg }}><span style={{ color: colors.text, fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontSize: '0.9rem' }}>Aa</span></div>
                  ) : (
                    <div className="h-12 rounded mb-2 flex items-center justify-center border border-dashed border-neutral-600"><Palette size={16} className="text-neutral-500" /></div>
                  )}
                  <span className="text-xs text-neutral-400 uppercase tracking-widest">{name}</span>
                </button>
              ))}
            </div>
            {content.settings?.theme === 'Custom' && (
              <div className="grid grid-cols-3 gap-3 p-3 bg-neutral-900 border border-neutral-800 rounded">
                <FG label="Background"><div className="flex gap-2 items-center"><input type="color" value={content.settings?.custom_theme?.bg || '#DCDDD8'} onChange={e => updateField('settings.custom_theme.bg', e.target.value)} className="w-8 h-8 rounded border border-neutral-700 bg-transparent cursor-pointer" /><Input value={content.settings?.custom_theme?.bg || ''} onChange={e => updateField('settings.custom_theme.bg', e.target.value)} className={`${IC} w-24`} /></div></FG>
                <FG label="Text"><div className="flex gap-2 items-center"><input type="color" value={content.settings?.custom_theme?.text || '#1C1F26'} onChange={e => updateField('settings.custom_theme.text', e.target.value)} className="w-8 h-8 rounded border border-neutral-700 bg-transparent cursor-pointer" /><Input value={content.settings?.custom_theme?.text || ''} onChange={e => updateField('settings.custom_theme.text', e.target.value)} className={`${IC} w-24`} /></div></FG>
                <FG label="Accent"><div className="flex gap-2 items-center"><input type="color" value={content.settings?.accent_color || '#2B3BE0'} onChange={e => updateField('settings.accent_color', e.target.value)} className="w-8 h-8 rounded border border-neutral-700 bg-transparent cursor-pointer" /><Input value={content.settings?.accent_color || ''} onChange={e => updateField('settings.accent_color', e.target.value)} className={`${IC} w-24`} /></div></FG>
              </div>
            )}
            <SH title="Headline Font" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {HEADLINE_FONTS.map(f => (
                <button key={f} onClick={() => updateField('settings.headline_font', f)}
                  className={`p-3 rounded border-2 transition-all ${content.settings?.headline_font === f ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-neutral-800 hover:border-neutral-600'}`}>
                  <span className="text-white text-lg block mb-1 truncate" style={{ fontFamily: `'${f}', serif`, fontStyle: f !== 'Anton' && f !== 'Bebas Neue' && f !== 'Space Grotesk' && f !== 'Archivo Black' ? 'italic' : 'normal' }}>Aa</span>
                  <span className="text-xs text-neutral-500 block truncate">{f}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><Switch checked={content.settings?.headline_italic !== false} onCheckedChange={v => updateField('settings.headline_italic', v)} /><span className="text-xs text-neutral-400">Italic</span></div>
              <div className="flex items-center gap-2"><Switch checked={content.settings?.headline_uppercase !== false} onCheckedChange={v => updateField('settings.headline_uppercase', v)} /><span className="text-xs text-neutral-400">Uppercase</span></div>
            </div>
            <SH title="Body Font" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {BODY_FONTS.map(f => (
                <button key={f} onClick={() => updateField('settings.body_font', f)}
                  className={`p-3 rounded border-2 transition-all ${content.settings?.body_font === f ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-neutral-800 hover:border-neutral-600'}`}>
                  <span className="text-white text-sm block mb-1" style={{ fontFamily: `'${f}', sans-serif` }}>The quick brown fox</span>
                  <span className="text-xs text-neutral-500">{f}</span>
                </button>
              ))}
            </div>
            <SH title="Accent Color" />
            <div className="flex items-center gap-4">
              <input type="color" value={content.settings?.accent_color || '#2B3BE0'} onChange={e => updateField('settings.accent_color', e.target.value)} className="w-12 h-12 rounded border border-neutral-800 cursor-pointer bg-transparent" />
              <Input value={content.settings?.accent_color || '#2B3BE0'} onChange={e => updateField('settings.accent_color', e.target.value)} className={`${IC} w-32`} maxLength={7} />
              <div className="h-10 px-6 flex items-center rounded text-sm font-semibold" style={{ backgroundColor: content.settings?.accent_color || '#2B3BE0', color: '#fff' }}>Preview</div>
            </div>
          </TabsContent>

          {/* ========== LINKS TAB ========== */}
          <TabsContent value="links" className="mt-6 space-y-4">
            <SH title="Social Links" />
            <p className="text-neutral-500 text-xs">Empty URLs are hidden on the public page.</p>
            {content.social_links?.map((link, idx) => (
              <div key={link.id || idx} className="p-3 bg-neutral-900 border border-neutral-800 rounded space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <FG label="Platform"><Input value={link.platform} onChange={e => updateField(`social_links.${idx}.platform`, e.target.value)} className={IC} /></FG>
                  <FG label="Handle"><Input value={link.handle} onChange={e => updateField(`social_links.${idx}.handle`, e.target.value)} className={IC} /></FG>
                  <div className="flex gap-2 items-end">
                    <FG label="URL" className="flex-1"><Input value={link.url} onChange={e => updateField(`social_links.${idx}.url`, e.target.value)} className={IC} placeholder="https://" /></FG>
                    <Button variant="ghost" size="icon" onClick={() => { setContent(prev => { const n = cloneDeep(prev); n.social_links.splice(idx, 1); setSaveStatus('unsaved'); debouncedSave(n); return n; }); }} className="text-red-400 hover:text-red-300 mb-0.5 h-9 w-9"><Trash2 size={14} /></Button>
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={() => { setContent(prev => { const n = cloneDeep(prev); n.social_links.push({ id: `link-${Date.now()}`, platform: 'New Platform', handle: '', url: '' }); setSaveStatus('unsaved'); debouncedSave(n); return n; }); }} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><Plus size={14} /> Add Link</Button>
          </TabsContent>


          {/* ========== SHOP TAB ========== */}
          <TabsContent value="shop" className="mt-6 space-y-4">
            <SH title="Store Status" />
            <StoreControl />
            <SH title="Products" />
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <Button onClick={async () => { try { await api.post('/admin/shop/sync'); toast.success('Products synced from Printful'); fetchShopProducts(); } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); } }} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><RotateCcw size={14} /> Sync from Printful</Button>
              {shopProducts.length > 0 && (() => {
                const lastSync = shopProducts.find(p => p.synced_at)?.synced_at;
                return lastSync ? <span className="text-xs text-neutral-600">Last synced: {new Date(lastSync).toLocaleString()}</span> : null;
              })()}
            </div>
            <p className="text-neutral-500 text-xs mb-2">New products arrive HIDDEN. Review and toggle visible when ready. Sync never overwrites your edits to name, description, price or order.</p>
            <ShopProductsList onRefresh={fetchShopProducts} products={shopProducts} />
            <SH title="Orders" />
            <OrdersList />
          </TabsContent>

          {/* ========== LEGAL TAB ========== */}
          <TabsContent value="legal" className="mt-6 space-y-4">
            <SH title="Legal Pages" />
            <p className="text-neutral-500 text-xs">Edit your Shipping, Returns, Privacy and Terms pages. These appear in the footer and during checkout.</p>
            <LegalPagesEditor />
          </TabsContent>


          {/* ========== SEO TAB ========== */}
          <TabsContent value="seo" className="mt-6 space-y-4">
            <SH title="Search Engine Optimization" />
            <FG label="Page Title"><Input value={content.seo?.title || ''} onChange={e => updateField('seo.title', e.target.value)} className={IC} /></FG>
            <FG label="Meta Description"><Textarea value={content.seo?.description || ''} onChange={e => updateField('seo.description', e.target.value)} className={`${IC} min-h-[80px]`} /></FG>
            <FG label="Share Image URL"><Input value={content.seo?.share_image || ''} onChange={e => updateField('seo.share_image', e.target.value)} className={IC} placeholder="https://..." /></FG>
            <SH title="Share Preview" />
            <div className="p-4 bg-neutral-900 border border-neutral-800 rounded">
              <p className="text-blue-400 text-sm truncate">{content.seo?.title || 'Page Title'}</p>
              <p className="text-neutral-400 text-xs mt-1 line-clamp-2">{content.seo?.description || 'Meta description...'}</p>
            </div>
          </TabsContent>

          {/* ========== HISTORY TAB ========== */}
          <TabsContent value="history" className="mt-6 space-y-4">
            <SH title="Published Versions" />
            {history.length === 0 ? <p className="text-neutral-500 text-sm">No published versions yet. Publish your first version to start tracking history.</p> : (
              <div className="space-y-2">
                {history.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 bg-neutral-900 border border-neutral-800 rounded">
                    <div>
                      <p className="text-sm text-white">Version {v.version_number}</p>
                      <p className="text-xs text-neutral-500">{v.published_at ? new Date(v.published_at).toLocaleString() : ''} by {v.published_by}</p>
                    </div>
                    <Button onClick={() => restoreVersion(v.id)} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><History size={14} /> Restore</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== SETTINGS TAB ========== */}
          <TabsContent value="settings" className="mt-6 space-y-4">
            <SH title="Change Password" />
            <ChangePasswordForm />
            <SH title="Admin Accounts" />
            <p className="text-neutral-500 text-xs mb-2">Maximum 2 admin accounts.</p>
            {admins.map(a => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-neutral-900 border border-neutral-800 rounded">
                <div><p className="text-sm text-white">{a.email}</p><p className="text-xs text-neutral-500">{a.role}</p></div>
                {a.id !== user?.id && <Button onClick={async () => { try { await api.delete(`/admin/users/${a.id}`); fetchAdmins(); toast.success('Admin removed'); } catch { toast.error('Failed'); } }} variant="ghost" size="sm" className="text-red-400 hover:text-red-300"><Trash2 size={14} /></Button>}
              </div>
            ))}
            {admins.length < 2 && <AddAdminForm onAdded={fetchAdmins} />}
            <SH title="Danger Zone" />
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-xs border-red-900 text-red-400 hover:text-red-300 hover:bg-red-950 gap-1.5"><RotateCcw size={14} /> Reset to Defaults</Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-neutral-900 border-neutral-800">
                <AlertDialogHeader><AlertDialogTitle className="text-white">Reset all content?</AlertDialogTitle><AlertDialogDescription className="text-neutral-400">This replaces all content, images and settings with the original defaults. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel className="bg-neutral-800 text-white hover:bg-neutral-700 border-neutral-700">Cancel</AlertDialogCancel><AlertDialogAction onClick={resetDefaults} className="bg-red-600 text-white hover:bg-red-700">Reset</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(false);
  const IC = "bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 text-sm";
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    setSaving(true);
    try { await api.post('/auth/change-password', { current_password: current, new_password: newPw }); toast.success('Password changed'); setCurrent(''); setNewPw(''); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
      <FG label="Current Password"><Input type="password" value={current} onChange={e => setCurrent(e.target.value)} className={IC} required /></FG>
      <FG label="New Password (12+ characters)"><Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className={IC} required minLength={12} /></FG>
      <Button type="submit" disabled={saving} size="sm" className="bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest gap-1"><Lock size={14} /> {saving ? 'Changing...' : 'Change Password'}</Button>
    </form>
  );
}

function AddAdminForm({ onAdded }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const IC = "bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 text-sm";
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/admin/users', { email, password: pw }); toast.success('Admin added'); setEmail(''); setPw(''); onAdded(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 bg-neutral-900 border border-neutral-800 rounded max-w-sm">
      <p className="text-xs text-neutral-400 uppercase tracking-widest font-semibold">Add Admin</p>
      <FG label="Email"><Input type="email" value={email} onChange={e => setEmail(e.target.value)} className={IC} required /></FG>
      <FG label="Password (12+ chars)"><Input type="password" value={pw} onChange={e => setPw(e.target.value)} className={IC} required minLength={12} /></FG>
      <Button type="submit" disabled={saving} size="sm" className="bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest gap-1"><Plus size={14} /> {saving ? 'Adding...' : 'Add Admin'}</Button>
    </form>
  );
}


function StoreControl() {
  const [storeOpen, setStoreOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    api.get('/admin/store-status').then(({ data }) => {
      setStoreOpen(data.open);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleToggle = async (newState) => {
    setToggling(true);
    try {
      const { data } = await api.post('/admin/store-toggle', { open: newState });
      setStoreOpen(data.open);
      toast.success(data.open ? 'Store is now open to the public' : 'Store is now closed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle store');
    } finally {
      setToggling(false);
    }
  };

  if (loading) return null;

  return (
    <div data-testid="store-control" className="p-4 rounded-lg border" style={{ borderColor: storeOpen ? '#16a34a' : '#d97706', backgroundColor: storeOpen ? 'rgba(22,163,74,0.08)' : 'rgba(217,119,6,0.08)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: storeOpen ? '#16a34a' : '#d97706' }} />
          <div>
            <p className="text-sm font-medium text-white" data-testid="store-status-text">
              Store is {storeOpen ? 'Open' : 'Closed'}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              {storeOpen ? 'Customers can browse and purchase products' : 'Public visitors see "Coming Soon"'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-500 uppercase tracking-widest">{storeOpen ? 'Open' : 'Closed'}</span>
          <Switch
            data-testid="store-toggle-switch"
            checked={storeOpen}
            disabled={toggling}
            onCheckedChange={handleToggle}
          />
        </div>
      </div>
    </div>
  );
}

function ShopProductsList({ products, onRefresh }) {
  const IC = "bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 text-sm";
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});
  const STRIPE_FEE_PCT = 2.9;
  const STRIPE_FEE_FIXED = 0.30;

  const toggleVisibility = async (pid, current) => {
    const newStatus = current ? 'active' : 'active';
    try {
      await api.put(`/admin/shop/products/${pid}`, { visible: !current, status: current ? 'active' : 'active' });
      toast.success(current ? 'Hidden' : 'Visible');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const saveEdits = async (pid) => {
    const e = edits[pid];
    if (!e) return;
    try {
      await api.put(`/admin/shop/products/${pid}`, e);
      toast.success('Saved');
      setEdits(prev => { const n = {...prev}; delete n[pid]; return n; });
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const moveProduct = async (idx, dir) => {
    const active = products.filter(p => p.status !== 'archived');
    const ni = idx + dir;
    if (ni < 0 || ni >= active.length) return;
    const order = active.map(p => p.printful_id);
    [order[idx], order[ni]] = [order[ni], order[idx]];
    try { await api.put('/admin/shop/products/reorder', { order }); onRefresh(); } catch { toast.error('Failed'); }
  };

  const setEdit = (pid, field, val) => setEdits(prev => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));

  const activeProducts = products.filter(p => p.status !== 'archived');
  const archivedProducts = products.filter(p => p.status === 'archived');

  const getWarnings = (p) => {
    const warnings = [];
    const variants = p.variants || p.pf_variants || [];
    const costs = p.pf_cost || p.cost || [];
    const hasNoPriceSet = variants.some(v => !v.retail_price || v.retail_price === '0' || v.retail_price === '0.00');
    if (hasNoPriceSet) warnings.push({ type: 'warn', text: 'No price set' });
    if (costs.length > 0 && variants.length > 0) {
      const firstCost = parseFloat(costs[0]?.printful_cost || costs[0]?.cost || '0');
      const firstRetail = parseFloat(variants[0]?.retail_price || '0');
      if (firstRetail > 0 && firstRetail <= firstCost) warnings.push({ type: 'error', text: 'Price below cost' });
    }
    const hasMockup = variants.some(v => (v.mockups || v.files || []).length > 0);
    if (!hasMockup && !p.pf_thumbnail_url && !p.thumbnail_url) warnings.push({ type: 'warn', text: 'No mockup' });
    if (p.status === 'new') warnings.push({ type: 'info', text: 'New — needs review' });
    return warnings;
  };

  const calcProfit = (retail, cost) => {
    const r = parseFloat(retail) || 0;
    const c = parseFloat(cost) || 0;
    if (r <= 0 || c <= 0) return null;
    const stripeFee = r * (STRIPE_FEE_PCT / 100) + STRIPE_FEE_FIXED;
    return (r - c - stripeFee).toFixed(2);
  };

  if (!activeProducts.length && !archivedProducts.length) return <p className="text-neutral-500 text-sm">No products. Click "Sync from Printful".</p>;

  return (
    <div className="space-y-2">
      {activeProducts.map((p, idx) => {
        const pid = p.printful_id;
        const name = edits[pid]?.custom_name ?? p.custom_name ?? '';
        const displayName = name || p.pf_name || p.name || '?';
        const ptype = p.pf_product_type || p.product_type || '?';
        const variants = p.variants || p.pf_variants || [];
        const costs = p.pf_cost || p.cost || [];
        const costStr = costs[0]?.printful_cost || costs[0]?.cost || '—';
        const retailStr = variants[0]?.retail_price || '—';
        const profit = calcProfit(retailStr, costStr);
        const warnings = getWarnings(p);
        const thumb = p.pf_thumbnail_url || p.thumbnail_url;
        const statusLabel = p.status === 'new' ? 'NEW' : p.visible ? 'VISIBLE' : 'HIDDEN';
        const statusColor = p.status === 'new' ? 'text-blue-400' : p.visible ? 'text-green-400' : 'text-neutral-500';
        const isExpanded = expanded === pid;

        return (
          <div key={pid} className="bg-neutral-900 border border-neutral-800 rounded overflow-hidden">
            {/* Summary row */}
            <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : pid)}>
              {thumb && <img src={thumb} alt={displayName} className="w-10 h-10 object-cover rounded flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{displayName}</span>
                  <span className="text-xs text-neutral-600 flex-shrink-0">{ptype}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-neutral-500 mt-0.5">
                  <span>{variants.length} vars</span>
                  <span>Cost ${costStr}</span>
                  <span>Retail ${retailStr}</span>
                  {profit !== null && <span className={parseFloat(profit) > 0 ? 'text-green-400' : 'text-red-400'}>Profit ${profit}</span>}
                  <span className={statusColor}>{statusLabel}</span>
                </div>
                {warnings.length > 0 && (
                  <div className="flex gap-2 mt-1">{warnings.map((w, i) => (
                    <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${w.type === 'error' ? 'bg-red-950 text-red-400' : w.type === 'warn' ? 'bg-yellow-950 text-yellow-400' : 'bg-blue-950 text-blue-400'}`}>{w.text}</span>
                  ))}</div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                <Button variant="ghost" size="icon" onClick={() => moveProduct(idx, -1)} disabled={idx === 0} className="text-neutral-500 hover:text-white h-7 w-7"><ArrowUp size={12} /></Button>
                <Button variant="ghost" size="icon" onClick={() => moveProduct(idx, 1)} disabled={idx === activeProducts.length - 1} className="text-neutral-500 hover:text-white h-7 w-7"><ArrowDown size={12} /></Button>
                <Switch checked={p.visible === true} onCheckedChange={() => toggleVisibility(pid, p.visible === true)} />
              </div>
            </div>
            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t border-neutral-800 p-3 space-y-3" onClick={e => e.stopPropagation()}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <FG label="Display Name"><Input value={edits[pid]?.custom_name ?? p.custom_name ?? ''} onChange={e => setEdit(pid, 'custom_name', e.target.value)} className={IC} placeholder={p.pf_name || p.name} /></FG>
                  <FG label="Product Group (for color combos)"><Input value={edits[pid]?.product_group ?? p.product_group ?? ''} onChange={e => setEdit(pid, 'product_group', e.target.value)} className={IC} placeholder="e.g. fzg-boxy-tee" /></FG>
                </div>
                <FG label="Description"><Textarea value={edits[pid]?.custom_description ?? p.custom_description ?? ''} onChange={e => setEdit(pid, 'custom_description', e.target.value)} className={`${IC} min-h-[60px]`} placeholder="Add description..." /></FG>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <FG label="Size Chart"><Textarea value={edits[pid]?.size_chart ?? p.size_chart ?? ''} onChange={e => setEdit(pid, 'size_chart', e.target.value)} className={`${IC} min-h-[40px]`} placeholder="S: 34-36&quot; | M: 38-40&quot;..." /></FG>
                  <FG label="Notes (shown to customer)"><Input value={edits[pid]?.notes ?? p.notes ?? ''} onChange={e => setEdit(pid, 'notes', e.target.value)} className={IC} placeholder="e.g. Boxy fit runs oversized" /></FG>
                </div>
                {/* Variants table */}
                <p className="text-xs text-neutral-400 uppercase tracking-widest font-semibold mt-2">Variants & Pricing</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-neutral-500 border-b border-neutral-800"><th className="text-left py-1 pr-2">Name</th><th className="text-left py-1 pr-2">Color</th><th className="text-left py-1 pr-2">Size</th><th className="text-right py-1 pr-2">Cost</th><th className="text-right py-1 pr-2">Retail</th><th className="text-right py-1">Profit</th></tr></thead>
                    <tbody>{variants.map((v, vi) => {
                      const vc = costs.find(c => c.variant_id === v.variant_id);
                      const vprofit = calcProfit(v.retail_price, vc?.printful_cost || vc?.cost);
                      return (
                        <tr key={v.variant_id} className="border-b border-neutral-800/50">
                          <td className="py-1.5 pr-2 text-neutral-300 truncate max-w-[160px]">{v.name?.split(' / ').slice(1).join(' / ') || v.name}</td>
                          <td className="py-1.5 pr-2 text-neutral-400">{v.color}</td>
                          <td className="py-1.5 pr-2 text-neutral-400">{v.size}</td>
                          <td className="py-1.5 pr-2 text-right text-neutral-500">${vc?.printful_cost || vc?.cost || '—'}</td>
                          <td className="py-1.5 pr-2 text-right text-white">${v.retail_price || '—'}</td>
                          <td className={`py-1.5 text-right ${vprofit && parseFloat(vprofit) > 0 ? 'text-green-400' : 'text-red-400'}`}>{vprofit ? `$${vprofit}` : '—'}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
                <p className="text-xs text-neutral-600">Profit = Retail − Cost − Stripe fee (2.9% + $0.30)</p>
                {edits[pid] && <Button onClick={() => saveEdits(pid)} size="sm" className="mt-2 bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest"><Save size={12} className="mr-1" /> Save Changes</Button>}
                {/* Gallery Manager */}
                <div className="mt-4 pt-4 border-t border-neutral-800">
                  <ProductGalleryManager product={p} onRefresh={onRefresh} />
                </div>
              </div>
            )}
          </div>
        );
      })}
      {archivedProducts.length > 0 && (
        <>
          <p className="text-xs text-neutral-500 uppercase tracking-widest mt-4">Archived ({archivedProducts.length})</p>
          {archivedProducts.map(p => (
            <div key={p.printful_id} className="p-2 bg-neutral-900/50 border border-neutral-800/50 rounded flex items-center gap-3 opacity-50">
              <span className="text-xs text-neutral-500 truncate">{p.pf_name || p.name}</span>
              <span className="text-xs text-neutral-600 ml-auto">Archived</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    const url = filter === 'needs_attention' ? '/admin/orders?status=needs_attention' : '/admin/orders';
    api.get(url).then(({ data }) => setOrders(data.orders || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  const retryPrintful = async (sessionId) => {
    try {
      await api.post(`/admin/orders/${sessionId}/retry-printful`);
      toast.success('Printful order retried');
      refreshOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Retry failed'); }
  };

  const processPayment = async (sessionId) => {
    try {
      const { data } = await api.post(`/admin/orders/${sessionId}/process-payment`);
      if (data.payment_status === 'paid') {
        toast.success(`Payment processed! Printful: ${data.printful_status || 'pending'}`);
      } else {
        toast.info(data.message || 'Payment not yet complete');
      }
      refreshOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Process failed'); }
  };

  const refundOrder = async (sessionId) => {
    if (!window.confirm('Issue a full refund for this order? This cannot be undone.')) return;
    try {
      const { data } = await api.post(`/admin/orders/${sessionId}/refund`);
      toast.success(`Refunded $${data.amount?.toFixed(2)} (${data.refund_id})`);
      refreshOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Refund failed'); }
  };

  const refreshOrders = async () => {
    const url = filter === 'needs_attention' ? '/admin/orders?status=needs_attention' : '/admin/orders';
    const { data } = await api.get(url);
    setOrders(data.orders || []);
  };

  const needsAttentionCount = orders.filter(o => o.printful_status === 'needs_attention').length;

  if (loading) return <p className="text-neutral-500 text-sm">Loading orders...</p>;

  return (
    <div className="space-y-3">
      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded transition-colors ${filter === 'all' ? 'bg-white text-black' : 'bg-neutral-900 text-neutral-400 hover:text-white border border-neutral-800'}`}>All Orders</button>
        <button data-testid="orders-filter-attention" onClick={() => setFilter('needs_attention')} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded transition-colors flex items-center gap-1.5 ${filter === 'needs_attention' ? 'bg-red-600 text-white' : 'bg-neutral-900 text-neutral-400 hover:text-white border border-neutral-800'}`}>
          Needs Attention
          {needsAttentionCount > 0 && filter !== 'needs_attention' && <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{needsAttentionCount}</span>}
        </button>
        <button onClick={refreshOrders} className="px-3 py-1.5 text-xs uppercase tracking-wider rounded bg-neutral-900 text-neutral-400 hover:text-white border border-neutral-800 transition-colors"><RotateCcw size={12} /></button>
      </div>

      {!orders.length ? (
        <p className="text-neutral-500 text-sm">{filter === 'needs_attention' ? 'No orders need attention.' : 'No orders yet.'}</p>
      ) : (
        <div className="space-y-2">
          {orders.map(o => {
            const orderNum = (o.stripe_session_id || '').slice(-8).toUpperCase();
            const total = parseFloat(o.total_amount || 0);
            const shipping = parseFloat(o.shipping_amount || 0);
            const addr = o.shipping_address || {};
            const createdAt = o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
            return (
              <div key={o.stripe_session_id} data-testid={`order-${orderNum}`} className={`p-3 bg-neutral-900 border rounded ${o.printful_status === 'needs_attention' ? 'border-red-800/50' : 'border-neutral-800'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-neutral-300">#{orderNum}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${o.payment_status === 'paid' ? 'bg-green-950 text-green-400' : o.payment_status === 'refunded' ? 'bg-purple-950 text-purple-400' : o.payment_status === 'expired' ? 'bg-neutral-800 text-neutral-500' : 'bg-yellow-950 text-yellow-400'}`}>{o.payment_status || 'pending'}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${o.printful_status === 'needs_attention' ? 'bg-red-950 text-red-400' : o.printful_order_id ? 'bg-green-950 text-green-400' : 'bg-neutral-800 text-neutral-500'}`}>
                        PF: {o.printful_status || (o.printful_order_id ? 'draft' : 'pending')}
                      </span>
                      {o.tracking_number && <span className="text-xs text-blue-400">Track: {o.tracking_number}</span>}
                    </div>
                    <p className="text-sm text-white mt-1">{o.customer_email || 'No email'}</p>
                    {addr.name && <p className="text-xs text-neutral-500">{addr.name}{addr.city ? `, ${addr.city}` : ''}{addr.state ? `, ${addr.state}` : ''}</p>}
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-neutral-400">{o.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-neutral-500">Subtotal: ${total.toFixed(2)}</span>
                      <span className="text-xs text-neutral-500">Shipping: ${shipping.toFixed(2)}</span>
                      <span className="text-xs text-white font-semibold">Total: ${(total + shipping).toFixed(2)}</span>
                    </div>
                    {o.printful_error && <p className="text-xs text-red-400 mt-1">Error: {o.printful_error}</p>}
                    <p className="text-[10px] text-neutral-600 mt-1">{createdAt}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {o.payment_status === 'pending' && (
                      <Button data-testid={`process-${orderNum}`} onClick={() => processPayment(o.stripe_session_id)} variant="outline" size="sm" className="text-xs border-blue-800 text-blue-400 hover:text-blue-300 gap-1"><RotateCcw size={12} /> Process</Button>
                    )}
                    {o.payment_status === 'paid' && !o.refund_id && (
                      <Button data-testid={`refund-${orderNum}`} onClick={() => refundOrder(o.stripe_session_id)} variant="outline" size="sm" className="text-xs border-purple-800 text-purple-400 hover:text-purple-300 gap-1"><CreditCard size={12} /> Refund</Button>
                    )}
                    {o.printful_status === 'needs_attention' && (
                      <Button data-testid={`retry-${orderNum}`} onClick={() => retryPrintful(o.stripe_session_id)} variant="outline" size="sm" className="text-xs border-red-800 text-red-400 hover:text-red-300 gap-1"><RotateCcw size={12} /> Retry</Button>
                    )}
                    {o.refund_id && <span className="text-[10px] text-purple-400">Refund: {o.refund_id}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function LegalPagesEditor() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/legal').then(({ data }) => setPages(data.pages || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startEdit = (page) => {
    setEditing(page.slug);
    setEditTitle(page.title);
    setEditContent(page.content);
  };

  const savePage = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/legal/${editing}`, { title: editTitle, content: editContent });
      toast.success(`${editTitle} saved`);
      setEditing(null);
      const { data } = await api.get('/admin/legal');
      setPages(data.pages || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-neutral-500 text-sm">Loading...</p>;

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm uppercase tracking-widest">{editing}</h3>
          <Button onClick={() => setEditing(null)} variant="ghost" size="sm" className="text-neutral-400"><X size={14} /> Cancel</Button>
        </div>
        <FG label="Page Title"><Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="bg-neutral-900 border-neutral-800 text-white" /></FG>
        <FG label="Page Content"><Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="bg-neutral-900 border-neutral-800 text-white min-h-[300px] font-mono text-sm" /></FG>
        <Button onClick={savePage} disabled={saving} className="bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest gap-1">
          <Save size={12} /> {saving ? 'Saving...' : 'Save Page'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {pages.map(p => (
        <div key={p.slug} className="flex items-center justify-between p-3 bg-neutral-900 border border-neutral-800 rounded">
          <div>
            <p className="text-sm text-white">{p.title}</p>
            <p className="text-xs text-neutral-500">
              /legal/{p.slug}
              {p.updated_at && ` · Updated ${new Date(p.updated_at).toLocaleDateString()}`}
              {!p.content && ' · Empty'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.open(`/legal/${p.slug}`, '_blank')} variant="ghost" size="sm" className="text-neutral-400 hover:text-white gap-1"><Eye size={12} /> View</Button>
            <Button onClick={() => startEdit(p)} variant="outline" size="sm" className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"><FileText size={12} /> Edit</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
